import { err, invalidState, notFound, ok, validate, type AppError, type Result } from '../errors/errors';
import type { Deps } from '../interfaces/stores';
import { NewRoundInputSchema, newRoundLabels, RoundSchema, TournamentSchema, type NewRoundInput, type Round, type RoundStatus, type Tournament } from '../schemas/entities';
import type { Format, SideIndex } from '../schemas/format';
import { resolveCoinFlip, type CoinFlipInput } from '../domain/sides';
import { DEFAULT_FORMAT, formatById } from '../formats';

type D<K extends keyof Deps> = Pick<Deps, K>;

export const createTournament = (d: D<'tournaments' | 'settings' | 'clock' | 'ids'>) => async (input: { name: string; formatId?: string; startDate?: string; endDate?: string }): Promise<Result<Tournament>> => {
  const now = d.clock.now();
  const v = validate(TournamentSchema, { id: d.ids.next(), name: input.name, formatId: input.formatId, ...days(input.startDate, input.endDate), createdAt: now, updatedAt: now }, TOURNAMENT_LABELS);
  if (!v.ok) return v;
  await d.tournaments.put(v.value);
  const s = await d.settings.get();
  await d.settings.put({ ...s, currentTournamentId: v.value.id });
  return v;
};

const TOURNAMENT_LABELS = { name: 'Tournament name', startDate: 'First day', endDate: 'Last day' };

/** Blank dates are "not set"; a single day needs no end date. */
const days = (startDate?: string, endDate?: string) => {
  const start = startDate?.trim() || undefined;
  const end = endDate?.trim() || undefined;
  return { startDate: start ?? end, endDate: start && end && end !== start ? end : undefined };
};

/** When the tournament runs (both blank clears them). */
export const setTournamentDates = (d: D<'tournaments' | 'clock'>) => async (id: string, startDate?: string, endDate?: string): Promise<Result<Tournament>> => {
  const t = await d.tournaments.get(id);
  if (!t) return err(notFound('tournament', id));
  const { startDate: _s, endDate: _e, ...rest } = t;
  const v = validate(TournamentSchema, { ...rest, ...days(startDate, endDate), updatedAt: d.clock.now() }, TOURNAMENT_LABELS);
  if (v.ok) await d.tournaments.put(v.value);
  return v;
};

/** Name and dates in one go (the Edit tournament dialog); `formatId` only for callers that still set one. Nothing changes unless all of it is valid. */
export const updateTournament = (d: D<'tournaments' | 'clock'>) => async (id: string, input: { name: string; formatId?: string; startDate?: string; endDate?: string }): Promise<Result<Tournament>> => {
  const t = await d.tournaments.get(id);
  if (!t) return err(notFound('tournament', id));
  if (input.formatId !== undefined && !formatById(input.formatId)) return err(notFound('format', input.formatId));
  const { startDate: _s, endDate: _e, ...rest } = t;
  const v = validate(TournamentSchema, { ...rest, name: input.name, ...(input.formatId ? { formatId: input.formatId } : {}), ...days(input.startDate, input.endDate), updatedAt: d.clock.now() }, TOURNAMENT_LABELS);
  if (v.ok) await d.tournaments.put(v.value);
  return v;
};

export const renameTournament = (d: D<'tournaments' | 'clock'>) => async (id: string, name: string): Promise<Result<Tournament>> => {
  const t = await d.tournaments.get(id);
  if (!t) return err(notFound('tournament', id));
  const v = validate(TournamentSchema, { ...t, name, updatedAt: d.clock.now() }, { name: 'Tournament name' });
  if (v.ok) await d.tournaments.put(v.value);
  return v;
};

/** The format new rounds in this tournament start with. */
export const setTournamentFormat = (d: D<'tournaments' | 'clock'>) => async (id: string, formatId: string): Promise<Result<Tournament>> => {
  const t = await d.tournaments.get(id);
  if (!t) return err(notFound('tournament', id));
  if (!formatById(formatId)) return err(notFound('format', formatId));
  const next = { ...t, formatId, updatedAt: d.clock.now() };
  await d.tournaments.put(next);
  return ok(next);
};

/**
 * Switch a round to another format, e.g. it was added as PF but turned out to be LD. Only before anything is on
 * the flow or the timer has run: a format change re-labels every column.
 */
export const changeRoundFormat = (d: D<'rounds' | 'flow' | 'timers' | 'clock' | 'tx'>) => (roundId: string, formatId: string): Promise<Result<Round>> =>
  d.tx.run(async () => {
    const r = await d.rounds.get(roundId);
    if (!r) return err(notFound('round', roundId));
    const format = formatById(formatId);
    if (!format) return err(notFound('format', formatId));
    if (r.format.id === format.id) return ok(r);
    const started = (await d.flow.cells(roundId)).some((c) => c.text.trim() || c.marks.length) || ((await d.timers.get(roundId))?.state.log.length ?? 0) > 0;
    if (started) return err(invalidState('round.formatLocked', "This round already has notes or timer history, so its format can't change. Add a new round instead."));
    const next = validate(RoundSchema, { ...r, format, firstSide: format.order === 'fixed' ? 0 : null, teamASide: r.teamASide, updatedAt: d.clock.now() });
    if (next.ok) await d.rounds.put(next.value);
    return next;
  });

export const createRound = (d: D<'rounds' | 'names' | 'clock' | 'ids' | 'tx'>) =>
  async (tournamentId: string, input: NewRoundInput, format: Format = DEFAULT_FORMAT): Promise<Result<Round>> => {
    const parsed = validate(NewRoundInputSchema, input, newRoundLabels);
    if (!parsed.ok) return parsed;
    const i = parsed.value;
    const now = d.clock.now();
    const round = validate(RoundSchema, {
      id: d.ids.next(),
      tournamentId,
      label: i.label,
      room: i.room,
      flight: i.flight,
      startTime: i.startTime,
      teamA: i.teamA,
      teamB: i.teamB,
      resolution: i.resolution,
      teamASide: null,
      firstSide: format.order === 'fixed' ? 0 : null,
      format,
      status: 'scheduled',
      createdAt: now,
      updatedAt: now,
    });
    if (!round.ok) return round;
    await d.tx.run(async () => {
      await d.rounds.put(round.value);
      await d.names.put({ roundId: round.value.id, teamA: i.teamANames, teamB: i.teamBNames, purgeAfter: null });
    });
    return round;
  };

export const updateRoundDetails = (d: D<'rounds' | 'names' | 'clock' | 'tx'>) =>
  async (roundId: string, input: NewRoundInput): Promise<Result<Round>> => {
    const r = await d.rounds.get(roundId);
    if (!r) return err(notFound('round', roundId));
    const parsed = validate(NewRoundInputSchema, input, newRoundLabels);
    if (!parsed.ok) return parsed;
    const i = parsed.value;
    const next = validate(RoundSchema, { ...r, label: i.label, room: i.room, flight: i.flight, startTime: i.startTime, teamA: i.teamA, teamB: i.teamB, resolution: i.resolution, updatedAt: d.clock.now() });
    if (!next.ok) return next;
    await d.tx.run(async () => {
      await d.rounds.put(next.value);
      const names = await d.names.get(roundId);
      await d.names.put({ roundId, teamA: i.teamANames, teamB: i.teamBNames, purgeAfter: names?.purgeAfter ?? null });
    });
    return next;
  };

/** Set just the resolution, e.g. a Parli topic announced as the round starts. */
export const setResolution = (d: D<'rounds' | 'clock'>) => async (roundId: string, resolution: string): Promise<Result<Round>> => {
  const r = await d.rounds.get(roundId);
  if (!r) return err(notFound('round', roundId));
  const next = validate(RoundSchema, { ...r, resolution, updatedAt: d.clock.now() }, { resolution: 'Resolution' });
  if (next.ok) await d.rounds.put(next.value);
  return next;
};

/**
 * What to prefill for a new round's resolution: the latest one used for the same format in this tournament. Nothing for
 * Parli, whose topic changes every round.
 */
export const suggestedResolution = (rounds: Pick<Round, 'format' | 'resolution' | 'createdAt'>[], formatId: string): string => {
  const same = rounds.filter((r) => r.format.id === formatId && r.format.event !== 'parli' && r.resolution?.trim()).sort((a, b) => b.createdAt - a.createdAt);
  return same[0]?.resolution ?? '';
};

export const recordCoinFlip = (d: D<'rounds' | 'clock'>) => async (roundId: string, flip: CoinFlipInput): Promise<Result<Round>> => {
  const r = await d.rounds.get(roundId);
  if (!r) return err(notFound('round', roundId));
  const { teamASide, firstSide } = resolveCoinFlip(flip);
  const next = { ...r, teamASide, firstSide, updatedAt: d.clock.now() };
  await d.rounds.put(next);
  return ok(next);
};

/** Direct assignment (e.g. sides were posted on the pairing). */
export const setSides = (d: D<'rounds' | 'clock'>) =>
  async (roundId: string, teamASide: SideIndex | null, firstSide: SideIndex | null): Promise<Result<Round>> => {
    const r = await d.rounds.get(roundId);
    if (!r) return err(notFound('round', roundId));
    const next = { ...r, teamASide, firstSide: r.format.order === 'fixed' ? 0 : firstSide, updatedAt: d.clock.now() };
    await d.rounds.put(next);
    return ok(next);
  };

const allowed: Record<RoundStatus, RoundStatus[]> = {
  scheduled: ['inRound', 'deciding', 'committed'],
  inRound: ['scheduled', 'deciding', 'committed'],
  deciding: ['inRound', 'committed'],
  committed: ['deciding', 'submitted'],
  submitted: ['committed'],
};

export const setRoundStatus = (d: D<'rounds' | 'clock'>) => async (roundId: string, status: RoundStatus): Promise<Result<Round>> => {
  const r = await d.rounds.get(roundId);
  if (!r) return err(notFound('round', roundId));
  if (r.status === status) return ok(r);
  if (!allowed[r.status].includes(status)) {
    return err(invalidState(`round.${r.status}->${status}`, `A round that is "${r.status}" can't move to "${status}".`));
  }
  const next = { ...r, status, updatedAt: d.clock.now() };
  await d.rounds.put(next);
  return ok(next);
};

export const deleteRound = (d: D<'rounds' | 'names' | 'flow' | 'timers' | 'ballots' | 'tx'>) => async (roundId: string): Promise<Result<true, AppError>> => {
  await d.tx.run(async () => {
    await d.flow.deleteRound(roundId);
    await d.timers.delete(roundId);
    await d.ballots.delete(roundId);
    await d.names.delete(roundId);
    await d.rounds.delete(roundId);
  });
  return ok(true);
};

/** Delete a tournament and everything in it (rounds, flows, timers, ballots, names). If it was current, nothing is current after. */
export const deleteTournament = (d: D<'tournaments' | 'rounds' | 'names' | 'flow' | 'timers' | 'ballots' | 'settings' | 'tx'>) =>
  async (id: string): Promise<Result<true>> => {
    const t = await d.tournaments.get(id);
    if (!t) return err(notFound('tournament', id));
    await d.tx.run(async () => {
      for (const r of await d.rounds.listByTournament(id)) await deleteRound(d)(r.id);
      await d.tournaments.delete(id);
      const s = await d.settings.get();
      if (s.currentTournamentId === id) await d.settings.put({ ...s, currentTournamentId: null });
    });
    return ok(true);
  };

/** Save the post-round recap. Only for submitted rounds: the recap must never be in front of a judge who is still deciding. */
export const saveRoundSummary = (d: D<'rounds' | 'clock'>) => async (roundId: string, text: string, model: string): Promise<Result<Round>> => {
  const r = await d.rounds.get(roundId);
  if (!r) return err(notFound('round', roundId));
  if (r.status !== 'submitted') return err(invalidState('summary.notSubmitted', 'Summaries are written after the ballot is submitted.'));
  const next = validate(RoundSchema, { ...r, summary: { text, model, createdAt: d.clock.now() }, updatedAt: d.clock.now() });
  if (next.ok) await d.rounds.put(next.value);
  return next;
};

/** Delete debater names whose retention window has passed. Run on app start. */
export const purgeExpiredNames = (d: D<'names' | 'clock'>) => async (): Promise<number> => {
  const due = await d.names.listDueForPurge(d.clock.now());
  for (const n of due) await d.names.put({ ...n, teamA: n.teamA.map(() => ''), teamB: n.teamB.map(() => ''), purgeAfter: null });
  return due.length;
};

/** An assignment the Tabroom watcher read off the judge's "Current ballots" page. */
export interface TabroomAssignment {
  round: string;
  event: 'pf' | 'ld' | 'policy' | 'parli' | 'other';
  room: string;
  startTime: string;
  flight: string;
  teamA: string;
  teamB: string;
  teamASide: 'first-side' | 'second-side' | 'unknown';
  tournament: string | null;
}

const EVENT_FORMAT: Record<TabroomAssignment['event'], string | null> = { pf: 'pf-chssa', ld: 'ld-chssa', policy: 'policy-chssa', parli: 'parli-chssa', other: null };

/**
 * Turn an assignment into a new round: the tournament it belongs to (found by name, else created), its format, and the
 * sides if Tabroom showed them. Values are cleaned to fit the round form's rules rather than rejected.
 */
export const importAssignment = (d: D<'tournaments' | 'rounds' | 'names' | 'settings' | 'clock' | 'ids' | 'tx'>) =>
  async (a: TabroomAssignment, fallbackTournamentId: string | null): Promise<Result<Round>> => {
    const name = a.tournament?.trim().slice(0, 80) || null;
    const existing = name ? (await d.tournaments.list()).find((t) => t.name.toLowerCase() === name.toLowerCase()) : null;
    let tournamentId = existing?.id ?? fallbackTournamentId;
    const formatId = EVENT_FORMAT[a.event] ?? existing?.formatId ?? DEFAULT_FORMAT.id;
    if (!tournamentId || (name && !existing)) {
      const t = await createTournament(d)({ name: name ?? 'From Tabroom', formatId });
      if (!t.ok) return t;
      tournamentId = t.value.id;
    }
    const time = /^([01]?\d|2[0-3]):[0-5]\d$/.test(a.startTime.trim()) ? a.startTime.trim().padStart(5, '0') : '';
    const round = await createRound(d)(tournamentId, {
      label: a.round.trim().slice(0, 20) || 'Round',
      room: a.room.trim().slice(0, 30),
      flight: a.flight.trim().slice(0, 4),
      startTime: time,
      teamA: { code: a.teamA.trim().slice(0, 40) || 'Team A' },
      teamB: { code: a.teamB.trim().slice(0, 40) || 'Team B' },
    }, formatById(formatId) ?? DEFAULT_FORMAT);
    if (!round.ok || a.teamASide === 'unknown') return round;
    return setSides(d)(round.value.id, a.teamASide === 'first-side' ? 0 : 1, round.value.firstSide);
  };
